import { useEffect, useMemo, useRef, useState } from 'react'
import { parseScheduleCsv } from '../lib/csvParser.js'
import { parseIcsCalendar } from '../lib/icsParser.js'
import { supabase } from '../lib/supabase.js'
import { matchStaffName, assignmentDedupeKey } from '../lib/rosterMatch.js'

// Modal for bulk-importing a schedule from a CSV (Google Sheets /
// Excel) or ICS (Google Calendar / Apple Calendar) export.
//
// Flow:
//   1. Pick file. Format auto-detected by extension.
//   2. Parse → rows with { name, shift_date, start_time, end_time, role }.
//   3. Match each row's `name` to an active staff member by
//      case-insensitive exact OR substring match. Surface unmatched
//      names so the manager can fix the file or skip them.
//   4. Dedupe against existing assignments in the date range so
//      re-uploading the same file doesn't double-book anyone.
//   5. Confirm → bulk insert.
//
// The match is heuristic on purpose: an ICS event titled "Sarah - cook"
// should match staff "Sarah Lopez". An exact-match-only flow is more
// fragile in practice.
export default function ScheduleImportModal({
  open,
  onClose,
  activeStaff,           // [{ id, name, role, hourly_wage }, ...]
  onImported,            // (count) → void  — called after success so parent can refresh
}) {
  const fileRef = useRef(null)
  const [filename, setFilename]   = useState('')
  const [parsed, setParsed]       = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  useEffect(() => {
    if (open) {
      setFilename('')
      setParsed(null)
      setError(null)
      setSubmitting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open])

  // Compute matches against staff. For each parsed row, attach
  // either a `staff_id` or a `match_status` ('exact' | 'fuzzy' | 'none').
  const matched = useMemo(() => {
    if (!parsed?.rows?.length) return null
    // Shared matcher (exact ∪ substring) so this flow and the photo
    // import (RosterScanModal) can never drift on what counts as a match.
    const rows = parsed.rows.map((r) => {
      const hit = matchStaffName(r.name, activeStaff)
      if (hit) {
        return { ...r, staff_id: hit.staff.id, staff_name: hit.staff.name, match: hit.match }
      }
      return { ...r, staff_id: null, staff_name: null, match: 'none' }
    })

    let exactCount = 0, fuzzyCount = 0, unmatchedCount = 0
    const unmatchedNames = new Set()
    for (const row of rows) {
      if (row.match === 'exact') exactCount++
      else if (row.match === 'fuzzy') fuzzyCount++
      else { unmatchedCount++; unmatchedNames.add(row.name) }
    }

    return {
      rows,
      exactCount, fuzzyCount, unmatchedCount,
      unmatchedNames: [...unmatchedNames],
      readyCount: exactCount + fuzzyCount,
    }
  }, [parsed, activeStaff])

  if (!open) return null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    try {
      const text = await file.text()
      const isIcs = /\.ics$/i.test(file.name) || text.trimStart().startsWith('BEGIN:VCALENDAR')
      const result = isIcs ? parseIcsCalendar(text) : parseScheduleCsv(text)
      // Normalize shape: ICS gives { rows, errors, eventCount }, CSV gives
      // { rows, columns, headers, errors }. Caller only needs rows + errors.
      setParsed({
        format: isIcs ? 'ics' : 'csv',
        rows: result.rows,
        errors: result.errors,
        // CSV-only metadata (kept for the detected-columns panel)
        columns: result.columns ?? null,
        headers: result.headers ?? null,
        eventCount: result.eventCount ?? null,
      })
      if (result.rows.length === 0) {
        setError(result.errors[0] ?? 'No usable rows found.')
      }
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleImportModal] parse failed', e2)
      setError('Could not read this file. Make sure it\u2019s a CSV or ICS export.')
    }
  }

  async function handleConfirm() {
    if (submitting || !matched?.readyCount) return
    setSubmitting(true)
    setError(null)
    try {
      // 1. Pull existing assignments in the import's date range so
      //    we can dedupe on (staff_id, shift_date, start_time).
      const dates = matched.rows
        .filter((r) => r.staff_id)
        .map((r) => r.shift_date)
      const minDate = dates.reduce((a, b) => (a < b ? a : b))
      const maxDate = dates.reduce((a, b) => (a > b ? a : b))
      const { data: existing, error: existErr } = await supabase
        .from('schedule_assignments')
        .select('staff_id, shift_date, start_time')
        .gte('shift_date', minDate)
        .lte('shift_date', maxDate)
      if (existErr) throw existErr
      const existingKeys = new Set(
        (existing ?? []).map((e) =>
          assignmentDedupeKey(e.staff_id, e.shift_date, e.start_time)
        )
      )

      // 2. Build insert batch — skip rows that already exist or didn't match.
      const inserts = []
      let skipped_dupe = 0, skipped_unmatched = 0
      for (const r of matched.rows) {
        if (!r.staff_id) { skipped_unmatched++; continue }
        const key = assignmentDedupeKey(r.staff_id, r.shift_date, r.start_time)
        if (existingKeys.has(key)) { skipped_dupe++; continue }
        inserts.push({
          staff_id:   r.staff_id,
          shift_date: r.shift_date,
          start_time: r.start_time,
          end_time:   r.end_time,
          role:       r.role,
          status:     'scheduled',
        })
      }

      // 3. Bulk insert.
      let inserted = 0
      if (inserts.length > 0) {
        const { error: insErr } = await supabase
          .from('schedule_assignments')
          .insert(inserts)
        if (insErr) throw insErr
        inserted = inserts.length
      }

      onImported?.({ inserted, skipped_dupe, skipped_unmatched })
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleImportModal] import failed', e2)
      setError('Could not save to the database. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Import schedule"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Import schedule</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
            <div className="mb-1 font-medium">Two formats supported:</div>
            <ul className="list-disc space-y-1 pl-5 text-[13px]">
              <li>
                <span className="font-medium">CSV from Google Sheets / Excel</span> —
                columns: Staff, Date, Start, End (Role optional). Times can be
                "5:00 PM" or "17:00".
              </li>
              <li>
                <span className="font-medium">ICS from Google Calendar / Apple Calendar</span> —
                each event's title is matched to a staff member's name.
                Export → "Settings and sharing" → "Export calendar" in Google.
              </li>
            </ul>
            <div className="mt-2 text-xs text-blue-900/70">
              Existing shifts at the same time aren't duplicated. Only "active"
              staff are matched — add staff in Settings first if needed.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="sched-file">
              Choose file
            </label>
            <input
              ref={fileRef}
              id="sched-file"
              type="file"
              accept=".csv,.ics,text/csv,text/calendar"
              onChange={handleFile}
              className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            {filename && (
              <p className="mt-1 truncate text-xs text-black/55">
                Selected: <span className="font-medium text-black/80">{filename}</span>
                {parsed?.format && (
                  <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                    {parsed.format}
                  </span>
                )}
              </p>
            )}
          </div>

          {/* CSV column detection panel */}
          {parsed?.format === 'csv' && parsed?.headers?.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
              <div className="mb-1 font-medium text-black/60">
                Detected columns
              </div>
              <div className="space-y-0.5">
                <DetectedRow label="Staff name" idx={parsed.columns?.nameIdx}  headers={parsed.headers} />
                <DetectedRow label="Date"       idx={parsed.columns?.dateIdx}  headers={parsed.headers} />
                <DetectedRow label="Start"      idx={parsed.columns?.startIdx} headers={parsed.headers} />
                <DetectedRow label="End"        idx={parsed.columns?.endIdx}   headers={parsed.headers} />
                <DetectedRow label="Role"       idx={parsed.columns?.roleIdx}  headers={parsed.headers} optional />
              </div>
            </div>
          )}

          {/* Match preview */}
          {matched && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-1 text-xs font-medium text-emerald-900/70">
                Preview
              </div>
              <div className="text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{matched.readyCount}</span>{' '}
                {matched.readyCount === 1 ? 'shift' : 'shifts'} ready to import
                {matched.exactCount + matched.fuzzyCount > 0 && (
                  <>
                    {' '}({matched.exactCount} exact, {matched.fuzzyCount} fuzzy match
                    {matched.fuzzyCount === 1 ? '' : 'es'})
                  </>
                )}.
                {matched.unmatchedCount > 0 && (
                  <>
                    {' '}<span className="font-medium tabular-nums text-red-700">
                      {matched.unmatchedCount}
                    </span>{' '}
                    will be skipped (no staff match).
                  </>
                )}
              </div>

              {matched.unmatchedNames.length > 0 && (
                <details className="mt-2 border-t border-emerald-200/60 pt-2 text-xs text-emerald-900/85">
                  <summary className="cursor-pointer font-medium">
                    Unmatched names ({matched.unmatchedNames.length})
                  </summary>
                  <ul className="mt-1 list-disc pl-5">
                    {matched.unmatchedNames.slice(0, 20).map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                  <p className="mt-1 text-[11px] text-emerald-900/70">
                    Add these as staff in Settings, then re-import. Or update
                    your file to use the staff names you already have.
                  </p>
                </details>
              )}
            </div>
          )}

          {/* Per-row warnings */}
          {parsed?.errors?.length > 0 && parsed.rows.length > 0 && (
            <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
              <summary className="cursor-pointer font-medium">
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} skipped during parsing
              </summary>
              <ul className="mt-2 space-y-1">
                {parsed.errors.slice(0, 20).map((msg, idx) => (
                  <li key={idx} className="leading-snug">{msg}</li>
                ))}
              </ul>
            </details>
          )}

          {!parsed && !error && (
            <p className="text-xs text-black/55">
              Importing the same shifts again is safe — duplicates (matched on
              staff + date + start time) are skipped automatically.
            </p>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !matched?.readyCount}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Importing\u2026'
              : matched?.readyCount
                ? `Import ${matched.readyCount} ${matched.readyCount === 1 ? 'shift' : 'shifts'}`
                : 'Pick a file first'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DetectedRow({ label, idx, headers, optional }) {
  const found = idx != null && idx >= 0
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/60">{label}{optional ? ' (optional)' : ''}</span>
      <span className={`truncate font-medium ${found ? 'text-emerald-700' : (optional ? 'text-black/40' : 'text-red-600')}`}>
        {found ? headers[idx] : (optional ? '\u2014' : 'not found')}
      </span>
    </div>
  )
}
